import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const location = useLocation();
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormSuccess(true);
      form.reset();
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData(form);
      if (honeypotInput) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9svrdllbrt1qcgajb6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于白洁情感文学精选',
    description: '白洁情感文学精选致力于为都市情感文学爱好者提供高质量的阅读体验。以少妇白洁系列为核心，从多维度探讨当代女性的情感世界。',
    url: 'https://up-tv.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: '白洁情感文学精选',
      description: '以少妇白洁为核心的都市情感文学内容平台',
      url: 'https://up-tv.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <Navbar />
      <main className="min-h-screen pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section id="brand-intro" className="mb-16 md:mb-20 scroll-mt-24">
            <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-10 text-center">
              关于我们
            </h1>
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <span className="text-xs text-primary-500 font-label bg-primary-50 px-3 py-1 rounded-full">
                  品牌介绍
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-3 mb-4">
                  白洁情感文学精选
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  白洁情感文学精选是一个专注于都市情感文学的内容平台。我们以广受读者关注的<strong>少妇白洁</strong>系列为核心，从故事赏析、人物解读、情感话题和文学评论等多个维度，深入探讨当代女性的情感世界。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  我们的编辑团队由一群热爱文学的资深读者组成，她们中有人经历过婚姻的波折，有人在寻找自我的路上摸索前行。正是因为这些真实的人生体验，让我们能够以更深刻、更共情的视角来解读白洁的故事。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  每一篇发布在本站的文章，都经过编辑团队的精心打磨。我们追求的不仅是内容的专业性和深度，更希望每一位读者都能在此找到情感的共鸣和心灵的慰藉。
                </p>
              </div>
              <div className="flex-1">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20literary%20workspace%20with%20bookshelf%20filled%20with%20books%2C%20warm%20afternoon%20light%20streaming%20through%20window%2C%20cozy%20armchair%2C%20wooden%20desk%20with%20manuscripts%2C%20cream%20and%20amber%20tones%2C%20peaceful%20atmosphere%2C%20shallow%20depth%20of%20field&width=700&height=500&seq=about-brand-baijie&orientation=landscape"
                    alt="白洁情感文学精选 - 品牌介绍"
                    title="白洁情感文学精选品牌介绍"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section id="reputation" className="mb-16 md:mb-20 py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 rounded-2xl scroll-mt-24">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <span className="text-xs text-accent-500 font-label bg-accent-50 px-3 py-1 rounded-full">
                  读者口碑
                </span>
                <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mt-3">
                  读者心中的白洁
                </h2>
                <p className="text-sm text-foreground-600 mt-2">来自全国各地读者的真挚分享</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-50 rounded-xl p-6 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-heading font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.location}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed italic">
                      "{t.quote}"
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">50万+</div>
                  <p className="text-xs text-foreground-600 mt-1">月度读者</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">200+</div>
                  <p className="text-xs text-foreground-600 mt-1">原创文章</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">98%</div>
                  <p className="text-xs text-foreground-600 mt-1">好评率</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">10万+</div>
                  <p className="text-xs text-foreground-600 mt-1">订阅用户</p>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="scroll-mt-24">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <span className="text-xs text-primary-500 font-label bg-primary-50 px-3 py-1 rounded-full">
                  联系我们
                </span>
                <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mt-3">
                  期待与您交流
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  如果您有任何问题、建议或投稿意向，欢迎通过以下方式与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">editor@baijiewenxue.com</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-600">白洁情感文学精选</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-600">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6 text-center">发送消息</h3>

                {formSuccess && (
                  <div className="mb-6 p-4 bg-accent-50 border border-accent-200 rounded-lg text-sm text-accent-800 text-center">
                    感谢您的留言！我们会在1-2个工作日内回复您。
                  </div>
                )}

                {formError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center">
                    {formError}
                  </div>
                )}

                <form onSubmit={handleContactSubmit} data-readdy-form="">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-label text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-label text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-label text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="mb-6">
                    <label htmlFor="contact-message" className="block text-sm font-label text-foreground-700 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-label transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '发送中...' : '发送消息'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}